import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

export default function WidgetClientForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const token = sessionStorage.getItem("tredex_widget_token");

  const [form, setForm] = useState({
    clientId: "",
    businessName: "",
    brandColor: "#6C63FF",
    knowledge: "",
    businessDescription: "",
    voice: true,
    report: false,
    reportCadence: "weekly",
    reportEmail: "",
    geo: true,
    portalEnabled: false,
    portalPassword: "",
    position: {
      corner: "bottom-right",
      offsetX: 20,
      offsetY: 20,
    },
  });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState(null); // { clientId, businessName, embedCode }

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleAuthFail = () => {
    sessionStorage.removeItem("tredex_widget_token");
    navigate("/admin/widget/login", { replace: true });
  };

  // Read an uploaded .txt/.md/.csv file and merge it into the knowledge base.
  const handleKnowledgeFileUpload = (e) => {
    const files = e?.target?.files;
    if (!files || files.length === 0) return;
    const file = files[0];
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result || "";
      const merged = form.knowledge ? form.knowledge + "\n\n" + text : text;
      set("knowledge", merged);
      // reset so the same file can be picked again if needed
      e.target.value = "";
    };
    reader.readAsText(file);
  };

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `/api/widget/admin/clients/${encodeURIComponent(id)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.status === 401) return handleAuthFail();
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed to load client");
        const c = data.client || {};
        setForm({
          clientId: c.clientId || "",
          businessName: c.businessName || "",
          brandColor: c.brandColor || "#6C63FF",
          knowledge: c.knowledge || "",
          businessDescription: c.businessDescription || "",
          voice: !!(c.features && c.features.voice),
          report: !!(c.features && c.features.report),
          reportCadence: (c.features && c.features.reportCadence) || "weekly",
          reportEmail: (c.features && c.features.reportEmail) || "",
          geo: (c.features && c.features.geo === false) ? false : true,
          portalEnabled: !!(c.clientLogin && c.clientLogin.enabled),
          portalPassword: "",
          position: (c.position && typeof c.position === "object")
            ? { corner: c.position.corner || "bottom-right", offsetX: c.position.offsetX ?? 20, offsetY: c.position.offsetY ?? 20 }
            : { corner: "bottom-right", offsetX: 20, offsetY: 20 },
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        businessName: form.businessName.trim(),
        brandColor: form.brandColor,
        knowledge: form.knowledge,
        businessDescription: form.businessDescription.trim(),
        features: {
          voice: !!form.voice,
          report: !!form.report,
          reportCadence: form.reportCadence || "weekly",
          reportEmail: form.reportEmail.trim(),
          geo: form.geo !== false,
        },
        clientLogin: {
          enabled: !!form.portalEnabled,
          password: form.portalPassword || undefined,
        },
        position: form.position || { corner: "bottom-right", offsetX: 20, offsetY: 20 },
      };
      if (isEdit) payload.clientId = id;
      else if (form.clientId.trim()) payload.clientId = form.clientId.trim();

      const res = await fetch(
        isEdit
          ? `/api/widget/admin/clients/${encodeURIComponent(id)}`
          : "/api/widget/admin/clients",
        {
          method: isEdit ? "PUT" : "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );
      if (res.status === 401) return handleAuthFail();
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Save failed");
      setCreated({
        clientId: data.clientId || (data.client && data.client.clientId) || id,
        businessName: payload.businessName,
        embedCode: data.embedCode,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const copyEmbed = () => {
    if (navigator.clipboard && created.embedCode)
      navigator.clipboard.writeText(created.embedCode);
  };

  if (created) {
    return (
      <>
        <SEO title="Client Saved" noIndex />
        <Navbar />
        <div className="dashboard-page">
          <div className="container">
            <div className="profile-card widget-success">
              <div style={{ textAlign: "center", marginBottom: "20px" }}>
                <i className="fas fa-check-circle" style={{ fontSize: "3.2rem", color: "#27C93F" }}></i>
                <h1 style={{ fontFamily: "var(--font-display)", marginTop: "12px" }}>
                  {isEdit ? "Client updated!" : "Client created!"}
                </h1>
                <p style={{ color: "var(--text-muted)" }}>
                  {created.businessName} ({created.clientId}) is live. Paste this into their website:
                </p>
              </div>
              <div className="code-box">
                <code>{created.embedCode}</code>
              </div>
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", justifyContent: "center", marginTop: "20px" }}>
                <button className="btn btn-primary" onClick={copyEmbed}>
                  <i className="fas fa-copy"></i> Copy Script
                </button>
                <Link to="/admin/widget/clients" className="btn btn-outline">
                  <i className="fas fa-arrow-left"></i> Back to Clients
                </Link>
              </div>
              <div style={{ marginTop: '18px', padding: '14px 16px', background: 'var(--bg-secondary)', border: '1px solid var(--border-light)', borderRadius: 'var(--radius-md)', fontSize: '0.88rem', color: 'var(--text-secondary)', lineHeight: 1.6, maxWidth: '640px', marginLeft: 'auto', marginRight: 'auto' }}>
                <strong>Optional — page-aware AI:</strong> If the client wants the AI to know which product/page the visitor is on, they can add a small <code>window.tredexWidget.context</code> snippet alongside the embed script. See the guide: <a href="/docs/widget-context" target="_blank" rel="noopener" style={{ color: 'var(--accent)', fontWeight: 600 }}>docs/widget-context</a>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }
return (
    <>
      <SEO title={isEdit ? "Edit Client" : "Add New Client"} noIndex />
      <Navbar />
      <div className="dashboard-page">
        <div className="container">
          <div className="dash-header">
            <div>
              <h1>
                <i className="fas fa-comment-dots"></i>{" "}
                {isEdit ? "Edit Client" : "Add New Client"}
              </h1>
              <p>
                {isEdit
                  ? `Updating ${id}`
                  : "Enter the business name and (optionally) upload a TXT file with all details. The client ID and embed script are generated automatically."}
              </p>
            </div>
            <Link to="/admin/widget/clients" className="btn btn-outline">
              <i className="fas fa-arrow-left"></i> Back
            </Link>
          </div>

          {error && (
            <div className="form-status error" style={{ display: "block", marginBottom: "20px" }}>
              {error}
            </div>
          )}

          {loading ? (
            <p style={{ padding: "40px", textAlign: "center", color: "var(--text-muted)" }}>
              Loading client...
            </p>
          ) : (
            <form onSubmit={handleSubmit} className="widget-form">
              <div className="profile-card widget-form-section">
                <h3>
                  <i className="fas fa-store"></i> Business
                </h3>
                <div className="widget-form-grid">
                  <label className="widget-field">
                    Business Name <span className="req">*</span>
                    <input
                      required
                      value={form.businessName}
                      onChange={(e) => set("businessName", e.target.value)}
                      placeholder="e.g. Asha Cookies"
                      disabled={isEdit}
                    />
                    <span className="widget-hint">
                      Client ID (e.g. "ashacookies") is generated automatically from this name.
                    </span>
                  </label>
                  {!isEdit && (
                    <label className="widget-field">
                      Client ID (optional, URL-safe override)
                      <input
                        value={form.clientId}
                        onChange={(e) => set("clientId", e.target.value)}
                        placeholder="auto-generated from business name"
                      />
                    </label>
                  )}
                  <label className="widget-field">
                    Brand Color
                    <input type="color" value={form.brandColor} onChange={(e) => set("brandColor", e.target.value)} />
                  </label>
                  <label className="widget-field">
                    What does this business do?
                    <textarea
                      rows={3}
                      value={form.businessDescription}
                      onChange={(e) => set("businessDescription", e.target.value)}
                      placeholder="e.g. Coffee shop in Chennai serving specialty coffee and pastries, or a real estate agency helping people find homes."
                      style={{ flex: 1 }}
                    />
                    <span className="widget-hint">
                      Short description so the AI knows what kind of business this is and can adjust its tone and recommendations.
                    </span>
                  </label>
                </div>
              </div>

              <div className="profile-card widget-form-section">
                <h3><i className="fas fa-arrows-alt"></i> Position</h3>
                <p style={{ color: "var(--text-muted)", marginBottom: "14px" }}>
                  Choose where the widget should appear on the page. Use offsets to fine-tune placement
                  if the site already has floating buttons in that corner.
                </p>
                <div className="widget-form-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
                  <label className="widget-field">
                    Corner / edge
                    <select
                      value={form.position?.corner || "bottom-right"}
                      onChange={(e) =>
                        set("position", { ...(form.position || {}), corner: e.target.value })
                      }
                    >
                      <option value="bottom-right">Bottom right</option>
                      <option value="bottom-left">Bottom left</option>
                      <option value="top-right">Top right</option>
                      <option value="top-left">Top left</option>
                      <option value="bottom-center">Bottom center</option>
                      <option value="top-center">Top center</option>
                    </select>
                  </label>
                  <label className="widget-field">
                    Horizontal offset (px)
                    <input
                      type="number"
                      min="0"
                      value={form.position?.offsetX ?? 20}
                      onChange={(e) =>
                        set("position", {
                          ...(form.position || {}),
                          offsetX: Math.max(0, parseInt(e.target.value || "0", 10)),
                        })
                      }
                    />
                  </label>
                  <label className="widget-field">
                    Vertical offset (px)
                    <input
                      type="number"
                      min="0"
                      value={form.position?.offsetY ?? 20}
                      onChange={(e) =>
                        set("position", {
                          ...(form.position || {}),
                          offsetY: Math.max(0, parseInt(e.target.value || "0", 10)),
                        })
                      }
                    />
                  </label>
                </div>
              </div>

              <div className="profile-card widget-form-section">
                <h3>
                  <i className="fas fa-file-alt"></i> Knowledge base (optional)
                </h3>
                <p style={{ color: "var(--text-muted)", marginBottom: "14px" }}>
                  Upload a <strong>.txt</strong> / .md / .csv file with the client's full details
                  (products, pricing, policies, FAQs, delivery, contact info). The AI uses this file
                  as its context for this client — no need to fill individual fields.
                </p>
                <label className="widget-field">
                  Business details text
                  <div style={{ display: "flex", gap: "10px", alignItems: "flex-end" }}>
                    <textarea
                      rows={6}
                      value={form.knowledge}
                      onChange={(e) => set("knowledge", e.target.value)}
                      placeholder="Paste the full business write-up here, or upload via the file picker."
                      style={{ flex: 1 }}
                    />
                    <input
                      type="file"
                      accept=".txt,.md,.csv,text/*"
                      onChange={handleKnowledgeFileUpload}
                      style={{ flex: "0 0 220px" }}
                    />
                  </div>
                  <span className="widget-hint" style={{ display: "block", marginTop: "6px" }}>
                    Only Business Name is required. The knowledge file is optional — when present, the
                    AI answers from it.
                  </span>
                  <span className="widget-hint" style={{ display: "block", marginTop: "6px" }}>
                    <strong>Tip:</strong> To make the AI page-aware (knowing which product/page the visitor is on), add a <code>window.tredexWidget.context</code> snippet on the client's site. See <a href="/docs/widget-context.md" target="_blank" rel="noopener" style={{ color: 'var(--accent)' }}>docs/widget-context.md</a>.
                  </span>
                </label>
              </div>

              <div className="profile-card widget-form-section">
                <h3><i className="fas fa-sliders-h"></i> Features &amp; Plan</h3>
                <div className="widget-form-grid" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
                  <label className="widget-field" style={{ flexDirection: "row", alignItems: "center", gap: "8px" }}>
                    <input type="checkbox" checked={!!form.voice} onChange={(e) => set("voice", e.target.checked)} />
                    🎙 Voice input
                  </label>
                  <label className="widget-field" style={{ flexDirection: "row", alignItems: "center", gap: "8px" }}>
                    <input type="checkbox" checked={!!form.report} onChange={(e) => set("report", e.target.checked)} />
                    📊 Reports
                  </label>
                  <label className="widget-field" style={{ flexDirection: "row", alignItems: "center", gap: "8px" }}>
                    <input type="checkbox" checked={form.geo !== false} onChange={(e) => set("geo", e.target.checked)} />
                    🌍 Geo capture
                  </label>
                </div>
                {!!form.report && (
                  <div className="widget-form-grid" style={{ marginTop: "12px" }}>
                    <label className="widget-field">
                      Report cadence
                      <select value={form.reportCadence} onChange={(e) => set("reportCadence", e.target.value)}>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </label>
                    <label className="widget-field">
                      Report recipient email
                      <input type="email" value={form.reportEmail} onChange={(e) => set("reportEmail", e.target.value)} placeholder="client@example.com" />
                    </label>
                  </div>
                )}
              </div>

              <div className="profile-card widget-form-section">
                <h3><i className="fas fa-lock"></i> Client Portal</h3>
                <div className="widget-form-grid">
                  <label className="widget-field" style={{ flexDirection: "row", alignItems: "center", gap: "8px" }}>
                    <input type="checkbox" checked={!!form.portalEnabled} onChange={(e) => set("portalEnabled", e.target.checked)} />
                    Enable client portal login
                  </label>
                  {!!form.portalEnabled && (
                    <label className="widget-field">
                      Password {isEdit ? "(leave blank to keep existing)" : ""}
                      <input type="text" value={form.portalPassword} onChange={(e) => set("portalPassword", e.target.value)} placeholder={"Enter password for this client"} />
                    </label>
                  )}
                </div>
                {form.clientId && !!form.portalEnabled && (
                  <p className="widget-hint" style={{ marginTop: "10px" }}>
                    Portal URL: <code>https&#x3A;&#x2F;&#x2F;tredextechnology.in&#x2F;client-portal&#x2F;{form.clientId}</code>
                  </p>
                )}
              </div>

              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Client"} <i className="fas fa-check"></i>
                </button>
                <Link to="/admin/widget/clients" className="btn btn-outline">
                  <i className="fas fa-times"></i> Cancel
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}